import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { EffectiveTheme, ProfessorTheme, ThemeCssVars } from '../_model/models';
type ThemeMode = EffectiveTheme['mode'];

const LIGHT_DEFAULTS: ThemeCssVars = {
  '--tangram-bg':          '#FFFFFF',
  '--tangram-surface':     '#F5F5F5',
  '--tangram-text':        '#111111',
  '--tangram-header-bg':   '#7C3AED',
  '--tangram-header-text': '#FFFFFF',
  '--tangram-primary':     '#7C3AED',
  '--tangram-secondary':   '#5B21B6',
  '--tangram-accent':      '#8B5CF6',
  '--tangram-button':        '#7C3AED',
  '--tangram-panel':         '#F5F5F5',
  '--tangram-primary-text':  '#FFFFFF',
  '--tangram-logo-filter':   'brightness(0) invert(1)',
  '--tangram-success':     '#22c55e',
  '--tangram-danger':      '#ef4444',
  '--tangram-gold':        '#eab308',
};

const CSS_VAR_STORAGE_KEY = 'tangram_css_vars';
const LIGHT_MODE_STORAGE_KEY = 'tangram_light_mode';
const MODE_STORAGE_KEY = 'tangram_active_mode';

/** Maps ProfessorTheme field names to CSS custom property names. */
const COLOR_FIELD_TO_VAR: Record<string, keyof ThemeCssVars> = {
  backgroundColor:    '--tangram-bg',
  surfaceColor:       '--tangram-surface',
  textColor:          '--tangram-text',
  headerBackground:   '--tangram-header-bg',
  headerTextColor:    '--tangram-header-text',
  primaryButtonColor: '--tangram-primary',
  primaryTextColor:   '--tangram-primary-text',
  secondaryColor:     '--tangram-secondary',
  accentColor:        '--tangram-accent',
  buttonColor:        '--tangram-button',
  cardBackground:     '--tangram-panel',
};

export const PRESET_THEMES: Record<string, Partial<ProfessorTheme> & { label: string }> = {
  OCEAN_BLUE: {
    label: 'Ocean Blue',
    primaryButtonColor: '#3737CD', primaryTextColor: '#FFFFFF',
    secondaryColor: '#1E1E6E',     accentColor: '#7C7CFF',
    backgroundColor: '#FFFFFF',    surfaceColor: '#F5F5F5', textColor: '#111111',
    headerBackground: '#3737CD',   headerTextColor: '#FFFFFF', buttonColor: '#3737CD',
    cardBackground: '#F0F0FF',
  },
  FOREST_GREEN: {
    label: 'Forest Green',
    primaryButtonColor: '#2E7D32', primaryTextColor: '#FFFFFF',
    secondaryColor: '#1B5E20',     accentColor: '#4CAF50',
    backgroundColor: '#F9FBF9',    surfaceColor: '#E8F5E9', textColor: '#1A2E1A',
    headerBackground: '#2E7D32',   headerTextColor: '#FFFFFF', buttonColor: '#2E7D32',
    cardBackground: '#E8F5E9',
  },
  SUNSET_ORANGE: {
    label: 'Sunset Orange',
    primaryButtonColor: '#E64A19', primaryTextColor: '#FFFFFF',
    secondaryColor: '#BF360C',     accentColor: '#FF7043',
    backgroundColor: '#FFF8F6',    surfaceColor: '#FBE9E7', textColor: '#2D1A14',
    headerBackground: '#E64A19',   headerTextColor: '#FFFFFF', buttonColor: '#E64A19',
    cardBackground: '#FBE9E7',
  },
  ROYAL_PURPLE: {
    label: 'Royal Purple',
    primaryButtonColor: '#6A1B9A', primaryTextColor: '#FFFFFF',
    secondaryColor: '#4A148C',     accentColor: '#AB47BC',
    backgroundColor: '#FAF5FF',    surfaceColor: '#F3E5F5', textColor: '#1A0A2E',
    headerBackground: '#6A1B9A',   headerTextColor: '#FFFFFF', buttonColor: '#6A1B9A',
    cardBackground: '#F3E5F5',
  },
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly baseUrl = `${environment.baseUrl}/themes`;
  private readonly userUrl = `${environment.baseUrl}/users`;

  private effectiveSubject = new BehaviorSubject<EffectiveTheme | null>(null);
  /** Stream of the currently active effective theme. Components subscribe to this. */
  public effective$ = this.effectiveSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ─── Core: fetch & apply ──────────────────────────────────────────────────

  /** Fetch the effective theme from the API and apply CSS variables to the DOM. */
  refreshFromApi() {
    return this.http.get<any>(`${this.baseUrl}/effective`).pipe(
      tap(res => {
        if (res && res.success && res.data) {
          this.applyEffective(res.data);
        }
      })
    );
  }

  /** Apply an effective theme payload: sets CSS custom properties on :root and stores state. */
  applyEffective(data: EffectiveTheme): void {
    const root = document.documentElement;

    // Respect the user's header toggle (light / dark) when the API is NOT serving
    // an active professor theme. If the API says `professor`, that always wins:
    // otherwise a stale `tangram_active_mode` / `tangram_light_mode` from before
    // "Apply theme" would keep forcing light defaults and the custom palette
    // would never stick (including after refresh).
    const savedMode = this._readModeFromStorage();
    let effectiveMode: ThemeMode;
    if (data.mode === 'professor') {
      effectiveMode = 'professor';
      try {
        localStorage.removeItem(LIGHT_MODE_STORAGE_KEY);
      } catch (e) {}
    } else if (savedMode === 'light') {
      effectiveMode = 'light';
    } else if (savedMode === 'dark') {
      effectiveMode = 'dark';
    } else {
      effectiveMode = data.mode;
    }

    if (effectiveMode === 'light') {
      // Light mode: always apply LIGHT_DEFAULTS, never API dark vars.
      Object.entries(LIGHT_DEFAULTS).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
      const lightVars: Record<string, string> = {};
      Object.keys(LIGHT_DEFAULTS).forEach(k => { lightVars[k] = (LIGHT_DEFAULTS as any)[k]; });
      this._saveCssVarsToStorage(lightVars);
    } else if (effectiveMode === 'dark') {
      // Dark mode: clear all theme CSS vars so the original dark design renders.
      Object.keys(LIGHT_DEFAULTS).forEach(key => {
        root.style.removeProperty(key);
      });
      root.style.removeProperty('--tangram-icon-filter');
      root.removeAttribute('data-tangram-theme');
      root.removeAttribute('data-tangram-mode');
      this._saveModeToStorage('dark');
    } else {
      // Professor mode: apply the API's theme vars.
      const vars = data.cssVars || LIGHT_DEFAULTS;
      // Normalise: the backend emits --tangram-panel and --tangram-card as aliases
      // for cardBackground. Older saved themes may only have --tangram-card.
      // Ensure both are always present so _theme-dynamic.scss (--tangram-panel)
      // and dashboard.component.scss (--tangram-card) both read the same value.
      const normalized: Record<string, string> = Object.assign({}, vars as any);
      if (normalized['--tangram-card'] && !normalized['--tangram-panel']) {
        normalized['--tangram-panel'] = normalized['--tangram-card'];
      } else if (normalized['--tangram-panel'] && !normalized['--tangram-card']) {
        normalized['--tangram-card'] = normalized['--tangram-panel'];
      }
      Object.entries(normalized).forEach(([key, value]) => {
        root.style.setProperty(key, value as string);
      });
      this._saveCssVarsToStorage(normalized);
    }

    root.setAttribute('data-tangram-theme', 'active');
    root.setAttribute('data-tangram-mode', effectiveMode);
    this._saveModeToStorage(effectiveMode);

    // Compute icon filter for the active primary color
    if (effectiveMode !== 'dark') {
      const primary = root.style.getPropertyValue('--tangram-primary').trim() || '#7C3AED';
      root.style.setProperty('--tangram-icon-filter', this._computeIconFilter(primary));
      const primaryText = root.style.getPropertyValue('--tangram-primary-text').trim() || '#ffffff';
      root.style.setProperty('--tangram-logo-filter', this._computeLogoFilter(primaryText));
    }

    this.effectiveSubject.next(data);
  }

  /** Reset to light-mode defaults and remove the theme attribute. */
  applyDefaults(): void {
    const root = document.documentElement;
    Object.entries(LIGHT_DEFAULTS).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    // Drop the inline icon filter from any prior custom theme so the SCSS
    // fallback (matching the default purple primary) takes over without a refresh.
    root.style.removeProperty('--tangram-icon-filter');
    root.removeAttribute('data-tangram-theme');
    root.removeAttribute('data-tangram-mode');
    this.effectiveSubject.next(null);
    // Save light defaults to localStorage
    const lightVars: Record<string, string> = {};
    Object.keys(LIGHT_DEFAULTS).forEach(k => { lightVars[k] = (LIGHT_DEFAULTS as any)[k]; });
    this._saveCssVarsToStorage(lightVars);
  }

  /**
   * Apply light mode CSS vars to :root inline styles immediately.
   * Called by the header toggle so the light mode colors update without a page refresh.
   */
  applyLightMode(): void {
    const root = document.documentElement;
    Object.entries(LIGHT_DEFAULTS).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    // Drop the inline icon filter from any prior custom theme so the SCSS
    // fallback (matching the default purple primary) takes over without a refresh.
    root.style.removeProperty('--tangram-icon-filter');
    const lightVars: Record<string, string> = {};
    Object.keys(LIGHT_DEFAULTS).forEach(k => { lightVars[k] = (LIGHT_DEFAULTS as any)[k]; });
    this._saveCssVarsToStorage(lightVars);
  }

  /**
   * Apply a specific mode directly — used by the header toggle for instant
   * visual feedback. Persists the choice, updates CSS vars / HTML attributes,
   * and notifies all subscribers so UI bindings stay in sync.
   */
  applyMode(mode: ThemeMode): void {
    const root = document.documentElement;
    if (mode === 'light') {
      this.applyLightMode();
      root.setAttribute('data-tangram-theme', 'active');
      root.setAttribute('data-tangram-mode', 'light');
    } else if (mode === 'dark') {
      // Remove all tangram CSS variables so dark mode's hardcoded styles take over.
      Object.keys(LIGHT_DEFAULTS).forEach(key => {
        root.style.removeProperty(key);
      });
      root.style.removeProperty('--tangram-icon-filter');
      root.removeAttribute('data-tangram-theme');
      root.removeAttribute('data-tangram-mode');
      try { localStorage.removeItem(CSS_VAR_STORAGE_KEY); } catch (e) {}
    } else {
      // professor — rely on whatever CSS vars are already in localStorage
      root.setAttribute('data-tangram-theme', 'active');
      root.setAttribute('data-tangram-mode', 'professor');
    }
    this._saveModeToStorage(mode);
    // Notify subscribers (header toggle, theme-settings badge, etc.)
    const current = this.effectiveSubject.value;
    // When switching to light/dark, the active professor theme is no longer
    // applied — clear `activeThemeId` so `isActive(theme)` checks (e.g. the
    // "Applied" badge / button on the Themes page) correctly return false
    // for the previously-active card after Reset to Default.
    const clearActiveId = mode !== 'professor';
    if (current) {
      this.effectiveSubject.next({
        ...current,
        mode,
        ...(clearActiveId ? { activeThemeId: null as any } : {}),
      });
    } else {
      this.effectiveSubject.next({ mode, palette: {}, cssVars: {}, activeThemeId: null } as any as EffectiveTheme);
    }
  }

  // ─── localStorage: prevent FOUC ──────────────────────────────────────────

  /**
   * Apply CSS vars from localStorage synchronously (call in constructor before
   * any async API call) to paint the correct theme on first render.
   */
  applyFromLocalStorage(): void {
    try {
      const root = document.documentElement;

      // Determine the active mode BEFORE applying CSS vars, so we know
      // which palette to use.
      const savedMode = this._readModeFromStorage();
      const prefersLight = this._readLightModePref();

      // Professor must be checked before light: otherwise a leftover
      // `tangram_light_mode` would force LIGHT_DEFAULTS and skip the stored palette.
      if (savedMode === 'professor') {
        const raw = localStorage.getItem(CSS_VAR_STORAGE_KEY);
        if (raw) {
          const vars: Record<string, string> = JSON.parse(raw);
          // Keep --tangram-panel / --tangram-card in sync for themes saved before
          // the backend was updated to emit both.
          if (vars['--tangram-card'] && !vars['--tangram-panel']) {
            vars['--tangram-panel'] = vars['--tangram-card'];
          } else if (vars['--tangram-panel'] && !vars['--tangram-card']) {
            vars['--tangram-card'] = vars['--tangram-panel'];
          }
          Object.entries(vars).forEach(([key, value]) => {
            root.style.setProperty(key, value);
          });
          const primaryText = root.style.getPropertyValue('--tangram-primary-text').trim() || '#ffffff';
          root.style.setProperty('--tangram-logo-filter', this._computeLogoFilter(primaryText));
          root.setAttribute('data-tangram-theme', 'active');
        }
        root.setAttribute('data-tangram-mode', 'professor');
      } else if (savedMode === 'dark') {
        Object.keys(LIGHT_DEFAULTS).forEach(key => {
          root.style.removeProperty(key);
        });
        root.removeAttribute('data-tangram-theme');
        root.removeAttribute('data-tangram-mode');
      } else if (savedMode === 'light' || prefersLight) {
        Object.entries(LIGHT_DEFAULTS).forEach(([key, value]) => {
          root.style.setProperty(key, value);
        });
        root.setAttribute('data-tangram-theme', 'active');
        root.setAttribute('data-tangram-mode', 'light');
      } else {
        root.removeAttribute('data-tangram-theme');
        root.removeAttribute('data-tangram-mode');
      }
    } catch (e) {
      // localStorage unavailable — ignore
    }
  }

  private _readLightModePref(): boolean {
    try {
      return localStorage.getItem(LIGHT_MODE_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  private _saveModeToStorage(mode: ThemeMode): void {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch (e) {}
  }

  private _readModeFromStorage(): ThemeMode | null {
    try {
      const mode = localStorage.getItem(MODE_STORAGE_KEY);
      if (mode === 'light' || mode === 'dark' || mode === 'professor') {
        return mode;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  /** Remove stored CSS vars (call on logout). */
  clearLocalStorage(): void {
    try {
      localStorage.removeItem(CSS_VAR_STORAGE_KEY);
      localStorage.removeItem(MODE_STORAGE_KEY);
      localStorage.removeItem(LIGHT_MODE_STORAGE_KEY);
    } catch (e) {}
  }

  /**
   * Persist the result of the header light/dark toggle so that
   * applyFromLocalStorage() re-asserts the correct mode on every navigation.
   *
   * The header toggle previously wrote only `tangram_light_mode` but never
   * updated `tangram_active_mode`. On the next NavigationEnd, applyFromLocalStorage()
   * would read the stale `tangram_active_mode='light'` and flip the page back —
   * the root cause of themes not persisting across route changes.
   *
   * Call this instead of touching localStorage directly in the header.
   */
  persistToggleState(isLight: boolean): void {
    try {
      if (isLight) {
        localStorage.setItem(LIGHT_MODE_STORAGE_KEY, '1');
        this._saveModeToStorage('light');
      } else {
        localStorage.removeItem(LIGHT_MODE_STORAGE_KEY);
        // Always save 'dark' — an explicit user toggle must always win over
        // any stored theme (professor or otherwise).
        this._saveModeToStorage('dark');
      }
    } catch (e) {}
  }

  // ─── Professor theme CRUD ─────────────────────────────────────────────────

  listThemes() {
    return this.http.get<any>(`${this.baseUrl}`);
  }

  createTheme(payload: Partial<ProfessorTheme>) {
    return this.http.post<any>(`${this.baseUrl}`, payload);
  }

  updateTheme(id: string, payload: Partial<ProfessorTheme>) {
    return this.http.put<any>(`${this.baseUrl}/${id}`, payload);
  }

  deleteTheme(id: string) {
    return this.http.delete<any>(`${this.baseUrl}/${id}`);
  }

  setDefaultTheme(id: string) {
    return this.http.post<any>(`${this.baseUrl}/${id}/set-default`, {});
  }

  unsetDefaultTheme() {
    return this.http.post<any>(`${this.baseUrl}/unset-default`, {});
  }

  uploadThemeAsset(id: string, file: File, assetType: 'logo' | 'banner') {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('assetType', assetType);
    return this.http.post<any>(`${this.baseUrl}/${id}/asset`, fd);
  }

  // ─── User preferences ────────────────────────────────────────────────────

  updatePreferences(payload: { systemAppearance?: string; selectedProfessorThemeId?: string | null }) {
    return this.http.put<any>(`${this.userUrl}/themePreferences`, payload);
  }

  /**
   * Clear the active professor theme and return to default dark/light mode.
   * Professors own their default theme (isDefault flag on ProfessorTheme);
   * students have a per-user selectedProfessorThemeId. Both need different
   * endpoints to truly "unapply" — calling the wrong one leaves the theme
   * stuck on the next refresh.
   */
  resetToDefault(isProfessor: boolean) {
    // Optimistic update: flip the UI to light mode synchronously so the user
    // sees the theme change instantly. The API call runs in parallel; if it
    // fails, the user is still in a valid (light) state and a subsequent
    // refreshFromApi will restore the canonical state.
    this.persistToggleState(true);
    this.applyMode('light');

    return isProfessor
      ? this.unsetDefaultTheme()
      : this.updatePreferences({ selectedProfessorThemeId: null });
  }

  updateProfessorPolicy(allowStudentThemeSwitch: boolean) {
    return this.http.put<any>(`${this.userUrl}/professorThemePolicy`, { allowStudentThemeSwitch });
  }

  linkStudentToProfessor(studentId: string, professorId: string) {
    return this.http.post<any>(`${this.userUrl}/linkStudentProfessor`, { studentId, professorId });
  }

  // ─── Live preview ─────────────────────────────────────────────────────────

  /** Apply a preset palette live (no API call) — for preview-before-save. */
  applyPreset(preset: Partial<ProfessorTheme>): void {
    this.applyColorsLive(preset);
  }

  /**
   * Write individual color fields directly to :root CSS vars for real-time preview.
   * Also persists the result to localStorage so "Apply" survives a page reload.
   */
  applyColorsLive(colors: Partial<ProfessorTheme>): void {
    const root = document.documentElement;
    const updatedVars: Record<string, string> = {};

    Object.entries(colors).forEach(([field, value]) => {
      const varName = COLOR_FIELD_TO_VAR[field];
      if (varName && value) {
        root.style.setProperty(varName, value as string);
        updatedVars[varName] = value as string;
        // --tangram-panel and --tangram-card are aliases for cardBackground.
        // Keep both in sync so dashboard.component.scss (--tangram-card) and
        // _theme-dynamic.scss (--tangram-panel) both reflect the same value.
        if (varName === '--tangram-panel') {
          root.style.setProperty('--tangram-card', value as string);
          updatedVars['--tangram-card'] = value as string;
        }
      }
    });

    root.setAttribute('data-tangram-theme', 'active');
    // Activate the gated CSS overrides in _theme-dynamic.scss so the selected
    // colors apply across the whole app, not just inline-styled preview nodes.
    root.setAttribute('data-tangram-mode', 'professor');
    // Persist professor mode so applyFromLocalStorage() re-asserts it on
    // navigation instead of reverting to the previously saved dark/light mode.
    this._saveModeToStorage('professor');

    // Compute icon filter for the active primary color
    const primary = root.style.getPropertyValue('--tangram-primary').trim() || '#7C3AED';
    root.style.setProperty('--tangram-icon-filter', this._computeIconFilter(primary));
    const primaryText = root.style.getPropertyValue('--tangram-primary-text').trim() || '#ffffff';
    const logoFilter = this._computeLogoFilter(primaryText);
    root.style.setProperty('--tangram-logo-filter', logoFilter);
    updatedVars['--tangram-logo-filter'] = logoFilter;

    // Merge with what's already in localStorage and save
    try {
      const existing: Record<string, string> = this._loadCssVarsFromStorage();
      const merged = Object.assign({}, existing, updatedVars);
      this._saveCssVarsToStorage(merged);
    } catch (e) {}

    // Notify subscribers (header dark toggle, theme-settings active badge, etc.)
    // that the palette changed, so they re-render without waiting for a full
    // API refresh. Emit even when current is null (dark mode) so the component's
    // effective$ subscription fires and updates isActiveTheme / badge state.
    const current = this.effectiveSubject.value;
    const mergedPalette = Object.assign({}, current ? current.palette : {}, colors);
    const mergedCssVars = Object.assign({}, current ? (current.cssVars || {}) : {}, updatedVars);
    if (current) {
      this.effectiveSubject.next(Object.assign({}, current, {
        palette: mergedPalette,
        cssVars: mergedCssVars,
        mode: 'professor' as ThemeMode,
      }));
    } else {
      this.effectiveSubject.next({
        mode: 'professor' as ThemeMode,
        palette: mergedPalette as any,
        cssVars: mergedCssVars as any,
        activeThemeId: null,
        logoUrl: null,
        bannerUrl: null,
      } as EffectiveTheme);
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  get currentTheme(): EffectiveTheme | null {
    return this.effectiveSubject.value;
  }

  private _saveCssVarsToStorage(vars: Record<string, string>): void {
    try {
      localStorage.setItem(CSS_VAR_STORAGE_KEY, JSON.stringify(vars));
    } catch (e) {}
  }

  private _loadCssVarsFromStorage(): Record<string, string> {
    try {
      const raw = localStorage.getItem(CSS_VAR_STORAGE_KEY);
      if (raw) { return JSON.parse(raw); }
    } catch (e) {}
    // Return LIGHT_DEFAULTS as a plain Record
    const defaults: Record<string, string> = {};
    Object.keys(LIGHT_DEFAULTS).forEach(k => { defaults[k] = (LIGHT_DEFAULTS as any)[k]; });
    return defaults;
  }

  /**
   * Compute a CSS filter string that transforms the pink source SVG icons (#d54ed2)
   * to the given target hex colour. Uses invert + sepia + saturate + hue-rotate.
   * Source icon hue ≈ 302° (pink). We rotate to the target hue.
   */
  private _computeLogoFilter(primaryTextHex: string): string {
    const h = primaryTextHex.replace('#', '');
    if (h.length !== 6) { return 'brightness(0) invert(1)'; }
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const lum = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return lum > 0.179 ? 'brightness(0) invert(1)' : 'brightness(0)';
  }

  private _computeIconFilter(hex: string): string {
    // The filter chain `invert(1) brightness(0.5) sepia(1) saturate(10)` transforms
    // the pink source icon to an effective starting hue of ~17° (orange-brown).
    // Empirical proof: purple #7C3AED has hue ≈262°; the known-working fallback
    // `hue-rotate(245deg)` implies starting hue = 262-245 = 17°.
    const EFFECTIVE_SOURCE_HUE = 17;
    // Parse hex to HSL hue
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    if (max !== min) {
      const d = max - min;
      if (max === r) { h = ((g - b) / d + (g < b ? 6 : 0)) * 60; }
      else if (max === g) { h = ((b - r) / d + 2) * 60; }
      else { h = ((r - g) / d + 4) * 60; }
    }
    const rotation = Math.round(h - EFFECTIVE_SOURCE_HUE);
    return `invert(1) brightness(0.5) sepia(1) saturate(10) hue-rotate(${rotation}deg)`;
  }
}
